import { useMutation } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';

import type { CustomSkillRecord } from '../../../../../queries/chats';
import { UNSHARE_CUSTOM_SKILL_FROM_USER } from '../../../../../queries/mutations';
import { useNovaContext } from '../../../providers';
import { useSettingsRemoval } from '../../settings-shared/use-settings-removal';
import { getRootCustomSkillUuid } from '../custom-skill-model';

export function useRemoveCustomSkillShare(
  skill: CustomSkillRecord,
  reloadShares: () => Promise<void>,
  onChanged: () => Promise<void>,
) {
  const { userUuid: actorUuid } = useNovaContext();
  const notify = useNotify();
  const skillUuid = getRootCustomSkillUuid(skill);
  const removal = useSettingsRemoval(skillUuid, true, actorUuid);
  const [unshare] = useMutation(UNSHARE_CUSTOM_SKILL_FROM_USER, { errorPolicy: 'none', fetchPolicy: 'no-cache' });
  const removeUser = (userUuid: string) =>
    removal.run(
      userUuid,
      async () => {
        const { data } = await unshare({ variables: { uuid: skillUuid, userUuid } });
        if (data?.unshareCustomSkillFromUser !== true)
          throw new Error('The skill revocation was not confirmed. Try again.');
      },
      async () => {
        // A refresh failure must not invite retrying a mutation that already succeeded.
        try {
          await reloadShares();
          await onChanged();
        } catch {
          notify.error('Access was revoked, but skill details could not refresh.');
        }
      },
      'Custom skill access removed.',
    );
  return { ...removal, removeUser };
}
